import { useRef, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Flex,
  IconButton,
  Input,
  Stack,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { AddCircleOutline, CloseCircleOutline } from '@aslanpay/portal-common-lib';

import { ANSWER_MAX, FAQ_LIMIT, QUESTION_MAX } from './FaqFields.types';

function FaqFields() {
  const {
    register,
    control,
    watch,
    trigger,
    formState: { errors, touchedFields },
  } = useFormContext();
  const { fields, append, remove, move } = useFieldArray({ control, name: 'faqs' });
  const dragIndex = useRef<number | null>(null);
  const dragOverIndex = useRef<number | null>(null);
  const [dragState, setDragState] = useState<{ from: number; over: number } | null>(null);

  return (
    <Stack spacing="20px" mt="10px">
      <Text textStyle="extraSmallMedium" color="brand.darkGray">
        Add questions and answers employees might have (max of {FAQ_LIMIT}).
      </Text>
      {fields.map((field, index) => {
        const question = watch(`faqs.${index}.question`) ?? '';
        const answer = watch(`faqs.${index}.answer`) ?? '';
        const faqErrors = (
          errors.faqs as
            | Record<string, { question?: { message?: string }; answer?: { message?: string } }>
            | undefined
        )?.[index];
        const faqTouched = (
          touchedFields.faqs as Record<string, { question?: boolean; answer?: boolean }> | undefined
        )?.[index];

        const isBeingDragged = dragState?.from === index;
        let translateY = 0;
        if (dragState && !isBeingDragged) {
          const { from, over } = dragState;
          if (from < over && index > from && index <= over) {
            translateY = -200;
          } else if (from > over && index >= over && index < from) {
            translateY = 200;
          }
        }

        return (
          <Box
            key={field.id}
            draggable
            cursor="grab"
            opacity={dragState && !isBeingDragged ? 0.4 : 1}
            transform={`translateY(${translateY}px)`}
            transition="transform 0.2s ease, opacity 0.15s"
            onDragStart={() => {
              dragIndex.current = index;
              dragOverIndex.current = index;
              setDragState({ from: index, over: index });
            }}
            onDragOver={(e) => {
              e.preventDefault();
              if (dragIndex.current !== null && dragOverIndex.current !== index) {
                dragOverIndex.current = index;
                setDragState({ from: dragIndex.current, over: index });
              }
            }}
            onDragEnd={() => {
              if (
                dragIndex.current !== null &&
                dragOverIndex.current !== null &&
                dragIndex.current !== dragOverIndex.current
              ) {
                move(dragIndex.current, dragOverIndex.current);
              }
              dragIndex.current = null;
              dragOverIndex.current = null;
              setDragState(null);
            }}
          >
            <Flex justify="space-between" align="center" mb="10px">
              <Flex align="center" gap="8px">
                <Text cursor="grab" userSelect="none" color="brand.darkGray" fontSize="16px">
                  ⠿
                </Text>
                <Text textStyle="smallSemiBold" color="brand.darkPurple">
                  FAQ {index + 1}
                </Text>
              </Flex>
              <Flex align="center" gap="8px">
                <Text textStyle="smallMedium" color="brand.mutedPurple">
                  {fields.length}/{FAQ_LIMIT}
                </Text>
                <IconButton
                  variant="link"
                  aria-label="Remove FAQ"
                  icon={<CloseCircleOutline boxSize="24px" color="brand.purple" />}
                  onClick={() => remove(index)}
                />
              </Flex>
            </Flex>
            <Stack spacing="8px">
              <Box>
                <Box position="relative">
                  <Input
                    placeholder="Question"
                    maxLength={QUESTION_MAX}
                    isInvalid={!!faqErrors?.question && !!faqTouched?.question}
                    {...register(`faqs.${index}.question`, {
                      onBlur: () => trigger(`faqs.${index}.question`),
                    })}
                  />
                  <Text
                    position="absolute"
                    bottom="5px"
                    right="5px"
                    textStyle="extraExtraSmallMedium"
                    color="brand.darkGray"
                  >
                    {question.length} / {QUESTION_MAX}
                  </Text>
                </Box>
                {faqErrors?.question?.message && faqTouched?.question && (
                  <Text textStyle="extraSmallRegular" color="system.error" mt="4px">
                    {faqErrors.question.message}
                  </Text>
                )}
              </Box>
              <Box>
                <Box position="relative">
                  <Textarea
                    placeholder="Answer"
                    maxLength={ANSWER_MAX}
                    rows={4}
                    resize="none"
                    isInvalid={!!faqErrors?.answer && !!faqTouched?.answer}
                    {...register(`faqs.${index}.answer`, {
                      onBlur: () => trigger(`faqs.${index}.answer`),
                    })}
                  />
                  <Text
                    position="absolute"
                    bottom="5px"
                    right="5px"
                    textStyle="extraExtraSmallMedium"
                    color="brand.darkGray"
                  >
                    {answer.length} / {ANSWER_MAX}
                  </Text>
                </Box>
                {faqErrors?.answer?.message && faqTouched?.answer && (
                  <Text textStyle="extraSmallRegular" color="system.error" mt="4px">
                    {faqErrors.answer.message}
                  </Text>
                )}
              </Box>
            </Stack>
            {index < fields.length - 1 && <Divider borderColor="brand.lightGray1" mt="20px" />}
          </Box>
        );
      })}
      {fields.length < FAQ_LIMIT && (
        <Button
          alignSelf="flex-start"
          variant="link"
          leftIcon={<AddCircleOutline boxSize="20px" />}
          onClick={() => append({ question: '', answer: '' })}
          isDisabled={fields.length >= FAQ_LIMIT}
          mt="5px"
          p="0"
        >
          New FAQ
        </Button>
      )}
    </Stack>
  );
}

export default FaqFields;
